/**
 * Advanced Analytics Service
 * Comprehensive user behavior tracking and business intelligence.
 * Uses the shared apiClient (Axios) for event delivery.
 */

import apiClient from './apiClient';

class AdvancedAnalytics {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.userId = null;
    this.userRole = null;
    this.agencyId = null;
    this.startTime = Date.now();
    this.events = [];
    this.pageViews = [];
    this.userJourney = [];
    this.performanceMetrics = {};
    this.isEnabled = this.checkIfEnabled();
    this.batchSize = 10;
    this.batchTimeout = 5000;
    this.batchTimer = null;

    this.initializeTracking();
  }

  checkIfEnabled() {
    return typeof window !== 'undefined' && (
      import.meta.env.PROD ||
      localStorage.getItem('parkpro-analytics') === 'true'
    );
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  setUserContext(user) {
    this.userId = user?.id || user?.userid;
    this.userRole = user?.role;
    this.agencyId = user?.agencyId || user?.useragentid;

    this.trackEvent('user_context_set', {
      userId: this.userId,
      userRole: this.userRole,
      agencyId: this.agencyId,
      timestamp: new Date().toISOString(),
    });
  }

  trackPageView(page = null) {
    const pageData = {
      page: page || window.location.pathname,
      title: document.title,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      userId: this.userId,
      userRole: this.userRole,
      agencyId: this.agencyId,
      url: window.location.href,
      viewport: { width: window.innerWidth, height: window.innerHeight },
    };

    this.pageViews.push(pageData);
    this.userJourney.push({ type: 'page_view', ...pageData });
    this.trackEvent('page_view', pageData);
  }

  trackEvent(eventName, properties = {}) {
    if (!this.isEnabled) return;

    const event = {
      eventName,
      properties: {
        ...properties,
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        userId: this.userId,
        userRole: this.userRole,
        agencyId: this.agencyId,
        page: window.location.pathname,
        url: window.location.href,
      },
    };

    this.events.push(event);
    this.userJourney.push({ type: 'custom_event', ...event });
    this.batchSendEvents();
  }

  trackUserInteractions() {
    document.addEventListener('click', (e) => {
      const element = e.target;
      const tagName = element.tagName.toLowerCase();

      if (tagName === 'button' || element.classList.contains('btn')) {
        this.trackEvent('button_click', {
          buttonText: element.textContent?.trim(),
          buttonId: element.id,
          buttonClass: element.className,
          href: element.href,
          target: element.target,
        });
      }

      if (tagName === 'a') {
        this.trackEvent('link_click', {
          linkText: element.textContent?.trim(),
          linkHref: element.href,
          linkTarget: element.target,
          isExternal: !element.href.includes(window.location.origin),
        });
      }
    });

    document.addEventListener('submit', (e) => {
      const form = e.target;
      this.trackEvent('form_submit', {
        formId: form.id,
        formClass: form.className,
        formAction: form.action,
        formMethod: form.method,
        fieldCount: form.elements.length,
      });
    });
  }

  trackBusinessEvents() {
    this.trackEvent('trip_creation_started', {
      source: 'questionnaire',
      timestamp: new Date().toISOString(),
    });
    this.trackEvent('itinerary_generation_started', {
      tripId: null,
      timestamp: new Date().toISOString(),
    });
  }

  trackConversion(eventName, value = null, currency = 'USD') {
    this.trackEvent('conversion', {
      conversionType: eventName,
      value,
      currency,
      timestamp: new Date().toISOString(),
    });
  }

  trackMilestone(milestone, data = {}) {
    this.trackEvent('milestone_reached', {
      milestone,
      ...data,
      timestamp: new Date().toISOString(),
    });
  }

  trackError(error, context = {}) {
    this.trackEvent('error_occurred', {
      errorMessage: error.message,
      errorStack: error.stack,
      errorType: error.name,
      context,
      timestamp: new Date().toISOString(),
    });
  }

  initializeTracking() {
    if (!this.isEnabled) return;

    this.trackPageView();
    this.setupEventListeners();
    this.trackUserInteractions();
    this.trackBusinessEvents();
  }

  setupEventListeners() {
    if (typeof window !== 'undefined' && window.performance) {
      window.addEventListener('load', () => {
        const perfData = window.performance.timing;
        this.performanceMetrics = {
          pageLoadTime: perfData.loadEventEnd - perfData.navigationStart,
          domContentLoaded: perfData.domContentLoadedEventEnd - perfData.navigationStart,
          timestamp: new Date().toISOString(),
        };
        this.trackEvent('performance_metrics', this.performanceMetrics);
      });
    }
  }

  batchSendEvents() {
    if (this.events.length >= this.batchSize) {
      this.sendEvents();
    } else if (!this.batchTimer) {
      this.batchTimer = setTimeout(() => this.sendEvents(), this.batchTimeout);
    }
  }

  async sendEvents() {
    if (this.events.length === 0) return;

    const eventsToSend = [...this.events];
    this.events = [];

    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    try {
      await apiClient.post('/api/analytics/events', {
        events: eventsToSend,
        sessionId: this.sessionId,
        userId: this.userId,
        userRole: this.userRole,
        agencyId: this.agencyId,
      });
    } catch (error) {
      console.warn('Error sending analytics events:', error);
      // Re-queue events for retry on next batch
      this.events.unshift(...eventsToSend);
    }
  }

  getAnalyticsData() {
    return {
      sessionId: this.sessionId,
      userId: this.userId,
      userRole: this.userRole,
      agencyId: this.agencyId,
      startTime: this.startTime,
      events: this.events,
      pageViews: this.pageViews,
      userJourney: this.userJourney,
      performanceMetrics: this.performanceMetrics,
    };
  }

  enable() {
    this.isEnabled = true;
    if (typeof window !== 'undefined') {
      localStorage.setItem('parkpro-analytics', 'true');
    }
  }

  disable() {
    this.isEnabled = false;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('parkpro-analytics');
    }
  }
}

export default new AdvancedAnalytics();
